// ── User Location (shared default for location-filtered pages) ────────────────
// One answer to "where is this user" shared by the dashboard, the Deals list and
// the Directory ("More") list, so every location filter defaults to the same
// place instead of each page starting at "All states".
//
// Resolution order:
//   1. Cached answer from an earlier page in this browser (localStorage, 12h TTL)
//   2. Browser geolocation — ONLY when the permission is ALREADY granted, so
//      these pages never raise a prompt of their own. The dashboard is where the
//      user is asked; every other page simply reuses that grant.
//   3. IP lookup via /api/geo-location (silent, city-level accuracy)
//
// Never throws: callers get `null` when the user can't be placed, and a page
// that gets `null` just shows everything.
// ─────────────────────────────────────────────────────────────────────────────

import axiosInst from '@/utils/axiosinstance';
import { US_STATE_NAMES } from '@/config/usStates.js';
import { getIPLocation } from './ipLocationService.js';

// v2: entries written before the country check could hold a non-US region code
// mapped onto a US state. Bumping the key retires those instead of waiting out
// their 12h TTL.
const STORAGE_KEY = 'renants:userLocation:v2';
const TTL_MS = 12 * 60 * 60 * 1000;   // a day-ish; a user rarely changes state mid-session
const GPS_TIMEOUT_MS = 6000;

let _promise = null;

// "New York" → "NY". Built once from the shared code→name map.
const NAME_TO_CODE = Object.entries(US_STATE_NAMES).reduce((map, [code, name]) => {
  map[name.toLowerCase()] = code;
  return map;
}, {});

/**
 * Normalise anything state-shaped ("NY", "ny", "New York") to the 2-letter code
 * the deals/directory facets are keyed by. Returns "" when unrecognised.
 */
export function toStateCode(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.length === 2 && US_STATE_NAMES[raw.toUpperCase()]) return raw.toUpperCase();
  return NAME_TO_CODE[raw.toLowerCase()] || '';
}

/** "US" / "us" / "USA" / "United States" → "US". Anything else → its own code. */
export function toCountryCode(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const upper = raw.toUpperCase();
  if (upper === 'US' || upper === 'USA' || upper === 'UNITED STATES'
      || upper === 'UNITED STATES OF AMERICA') return 'US';
  return upper.length === 2 ? upper : raw;
}

/**
 * The state code, but ONLY when it really is a US state.
 *
 * Two-letter subdivision codes are not unique across countries: a visitor in
 * Tamil Nadu, Goa, Odisha, Manipur, Chhattisgarh, Arunachal Pradesh or Ladakh
 * gets region_code TN / GA / OR / MN / CT / AR / LA from the IP provider —
 * every one of which is also a US state, and every one of which is present in
 * the deals and directory facets. Taking the code at face value silently filed
 * a Chennai visitor under Tennessee. So a bare code is trusted only when the
 * provider also says the country is the US; without a country we accept a
 * spelled-out name ("New York"), which is unambiguous, and nothing else.
 */
function usStateCode(value, country) {
  const cc = toCountryCode(country);
  if (cc === 'US') return toStateCode(value);
  if (cc) return '';                                    // some other country
  return NAME_TO_CODE[String(value || '').trim().toLowerCase()] || '';
}

function readCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || !entry.ts || Date.now() - entry.ts > TTL_MS) return null;
    const loc = entry.loc;
    if (!loc || (!loc.city && !loc.state)) return null;
    return loc;
  } catch (_) {
    return null;
  }
}

function writeCache(loc) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), loc }));
  } catch (_) { /* private mode / quota — the in-memory promise still serves this page */ }
  return loc;
}

/**
 * Publish a location resolved elsewhere (the dashboard's geolocation prompt or
 * its IP fallback) so the Deals and Directory pages can default to it.
 * Accepts either an already-shaped location or a raw {city, region_code, ...}
 * IP payload. GPS-derived values win over IP-derived ones.
 */
export function setUserLocation(loc) {
  if (!loc) return null;
  const country = toCountryCode(loc.country || loc.country_code);
  const shaped = {
    city: String(loc.city || '').trim(),
    state: usStateCode(loc.state || loc.region_code || loc.region, country),
    country,
    lat: loc.lat != null ? Number(loc.lat) : null,
    lng: loc.lng != null ? Number(loc.lng) : null,
    source: loc.source || 'ip',
  };
  if (!shaped.city && !shaped.state) return null;
  shaped.label = loc.label || [shaped.city, shaped.state].filter(Boolean).join(', ');

  // Don't let a coarse IP guess overwrite a precise GPS answer we already have.
  const existing = readCache();
  if (existing && existing.source === 'gps' && shaped.source !== 'gps') return existing;

  _promise = Promise.resolve(shaped);
  return writeCache(shaped);
}

/**
 * Publish coords the caller already has (the dashboard's geolocation success)
 * by reverse-geocoding them into a city/state once and caching the result.
 * Safe to fire-and-forget; a GPS answer already on file short-circuits it.
 */
export async function captureCoords(lat, lng) {
  if (lat == null || lng == null) return null;
  const cached = readCache();
  if (cached && cached.source === 'gps') return cached;
  const loc = await _reverseGeocode(Number(lat), Number(lng));
  if (!loc) return null;
  _promise = Promise.resolve(loc);
  return writeCache(loc);
}

/** Drop the remembered location (logout / "detect again"). */
export function clearUserLocation() {
  _promise = null;
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* nothing to clear */ }
}

/**
 * Resolve the user's location.
 * @param {{timeoutMs?: number}} opts  Cap on how long a caller will wait; the
 *   underlying lookup keeps running so a later page gets the cached answer.
 * `state` is filled only for US visitors — see usStateCode() — so a caller can
 * use it as a filter value without re-checking the country.
 * @returns {Promise<{city, state, country, lat, lng, label, source}|null>}
 */
export function getUserLocation({ timeoutMs = 3000 } = {}) {
  if (!_promise) {
    const pending = _resolve()
      .catch(() => null)
      .then((loc) => {
        // Don't memoise a failed lookup — let a later page try again. Only
        // clear it if nothing else (setUserLocation/captureCoords) took over.
        if (!loc && _promise === pending) _promise = null;
        return loc;
      });
    _promise = pending;
  }
  const inflight = _promise;
  if (!timeoutMs) return inflight;
  return Promise.race([
    inflight,
    new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs)),
  ]);
}

async function _resolve() {
  const cached = readCache();
  if (cached) return cached;

  // ── Browser geolocation, but only on an existing grant ────────────────────
  if (await _geolocationAlreadyGranted()) {
    const pos = await _currentPosition();
    if (pos) {
      const loc = await _reverseGeocode(pos.lat, pos.lng);
      if (loc) return writeCache(loc);
    }
  }

  // ── Silent IP fallback ────────────────────────────────────────────────────
  const ip = await getIPLocation();
  if (ip) {
    const country = toCountryCode(ip.country || ip.country_code);
    const loc = {
      city: String(ip.city || '').trim(),
      state: usStateCode(ip.region_code, country) || usStateCode(ip.region, country),
      country,
      lat: ip.lat != null ? Number(ip.lat) : null,
      lng: ip.lng != null ? Number(ip.lng) : null,
      label: ip.label || [ip.city, ip.region].filter(Boolean).join(', '),
      source: 'ip',
    };
    if (loc.city || loc.state) return writeCache(loc);
  }
  return null;
}

// True only when the user has already allowed location access (on the
// dashboard). Browsers without the Permissions API report false rather than
// risk an unexpected prompt on a list page.
async function _geolocationAlreadyGranted() {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return false;
  if (!navigator.permissions || !navigator.permissions.query) return false;
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state === 'granted';
  } catch (_) {
    return false;
  }
}

function _currentPosition() {
  return new Promise((resolve) => {
    let settled = false;
    const done = (v) => { if (!settled) { settled = true; resolve(v); } };
    setTimeout(() => done(null), GPS_TIMEOUT_MS);
    navigator.geolocation.getCurrentPosition(
      (pos) => done({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => done(null),
      { timeout: GPS_TIMEOUT_MS, maximumAge: 10 * 60 * 1000 }
    );
  });
}

/**
 * Coords → { city, state }. /api/reverse-geocode returns Google-shaped
 * components for both providers; Nominatim gives the state's full name in
 * short_name, so both spellings are run through toStateCode().
 */
async function _reverseGeocode(lat, lng) {
  try {
    const { data } = await axiosInst.post('/api/reverse-geocode', { latitude: lat, longitude: lng });
    const comps = Array.isArray(data && data.components) ? data.components : [];
    const pick = (type) => comps.find((c) => Array.isArray(c.types) && c.types.includes(type));
    const cityComp = pick('locality') || pick('postal_town') || pick('administrative_area_level_2');
    const stateComp = pick('administrative_area_level_1');
    const countryComp = pick('country');
    const city = String((cityComp && cityComp.long_name) || '').trim();
    const country = toCountryCode((countryComp && countryComp.short_name)
      || (countryComp && countryComp.long_name));
    const state = usStateCode(stateComp && stateComp.short_name, country)
      || usStateCode(stateComp && stateComp.long_name, country);
    if (!city && !state) return null;
    return {
      city,
      state,
      country,
      lat,
      lng,
      label: [city, state].filter(Boolean).join(', '),
      source: 'gps',
    };
  } catch (_) {
    return null;
  }
}
